import Phaser from 'phaser';
import { GAME_CONFIG } from '../config';
import { Player } from '../entities/Player';
import { virtualInput } from '../input/VirtualInput';
import {
  canMoveTo,
  findInteractableAt,
  getNextTile,
  type CollisionRect,
  type Direction,
  type Interactable,
  type MovementContext,
  type TileCoord,
} from '../logic/movement';

/** Keyboard reads, like `Direction`, with no diagonal: first match wins. */
const DIRECTION_ORDER: Direction[] = ['up', 'down', 'left', 'right'];

/**
 * Tiled's object `properties` array is typed `any` by Phaser's own
 * typings — this is the one place we narrow it, with an explicit shape for
 * the string-valued custom properties our map actually uses.
 */
interface TiledPropertyEntry {
  name: string;
  value: unknown;
}

function getTiledStringProperty(obj: Phaser.Types.Tilemaps.TiledObject, propertyName: string): string | undefined {
  const properties = obj.properties as TiledPropertyEntry[] | undefined;
  const entry = properties?.find((p) => p.name === propertyName);
  return typeof entry?.value === 'string' ? entry.value : undefined;
}

function tiledObjectToTile(obj: Phaser.Types.Tilemaps.TiledObject, tileSize: number): TileCoord {
  return {
    x: Math.floor((obj.x ?? 0) / tileSize),
    y: Math.floor((obj.y ?? 0) / tileSize),
  };
}

export class LibraryScene extends Phaser.Scene {
  private player!: Player;
  private movementContext!: MovementContext;
  private interactables: Interactable[] = [];

  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private keyW!: Phaser.Input.Keyboard.Key;
  private keyA!: Phaser.Input.Keyboard.Key;
  private keyS!: Phaser.Input.Keyboard.Key;
  private keyD!: Phaser.Input.Keyboard.Key;
  private keyInteract!: Phaser.Input.Keyboard.Key;
  private keyB!: Phaser.Input.Keyboard.Key;

  constructor() {
    super({ key: 'LibraryScene' });
  }

  create(): void {
    const { TILE_SIZE } = GAME_CONFIG;

    const map = this.make.tilemap({ key: 'library-map' });
    const tileset = map.addTilesetImage('bothy-tiles', 'tiles-atlas', TILE_SIZE, TILE_SIZE);
    if (!tileset) {
      throw new Error('Failed to load the "bothy-tiles" tileset — check public/atlases/tiles.png and public/maps/library.json.');
    }

    // createLayer()'s declared return type is a CPU/GPU-layer union because
    // of an optional `gpu` argument we never pass — we only ever get the
    // plain TilemapLayer back here, which is the one with hasTileAt/getTileAt.
    map.createLayer('floor', tileset, 0, 0);
    const wallsLayer = map.createLayer('walls', tileset, 0, 0) as Phaser.Tilemaps.TilemapLayer;
    const furnitureLayer = map.createLayer('furniture', tileset, 0, 0) as Phaser.Tilemaps.TilemapLayer;

    const collisionObjects = map.getObjectLayer('collision')?.objects ?? [];
    const collisionRects: CollisionRect[] = collisionObjects.map((obj) => ({
      x: obj.x ?? 0,
      y: obj.y ?? 0,
      width: obj.width ?? TILE_SIZE,
      height: obj.height ?? TILE_SIZE,
    }));

    this.interactables = (map.getObjectLayer('interact')?.objects ?? []).map((obj) => ({
      tile: tiledObjectToTile(obj, TILE_SIZE),
      name: obj.name,
      message: getTiledStringProperty(obj, 'message'),
    }));

    const spawnObject = map.getObjectLayer('spawn')?.objects[0];
    const spawnTile: TileCoord = spawnObject ? tiledObjectToTile(spawnObject, TILE_SIZE) : { x: 1, y: 1 };

    this.movementContext = {
      mapWidthInTiles: map.width,
      mapHeightInTiles: map.height,
      tileSize: TILE_SIZE,
      isTileSolid: (tile) => wallsLayer.hasTileAt(tile.x, tile.y) || furnitureLayer.hasTileAt(tile.x, tile.y),
      collisionRects,
    };

    this.player = new Player(this, spawnTile, TILE_SIZE);

    // "above" renders last so it draws over the player (e.g. the hanging lamp).
    map.createLayer('above', tileset, 0, 0);

    const mapPixelWidth = map.widthInPixels;
    const mapPixelHeight = map.heightInPixels;
    this.cameras.main.setBounds(0, 0, mapPixelWidth, mapPixelHeight);
    this.cameras.main.startFollow(this.player, true, 1, 1);
    this.cameras.main.setRoundPixels(true);

    this.setupInput();

    this.scene.launch('UIScene');
  }

  private setupInput(): void {
    const keyboard = this.input.keyboard;
    if (!keyboard) return;

    this.cursors = keyboard.createCursorKeys();
    this.keyW = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W);
    this.keyA = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A);
    this.keyS = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.S);
    this.keyD = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D);
    // Z/X for the A/B action buttons — W/A/S/D are already taken by movement.
    this.keyInteract = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.Z);
    this.keyB = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.X);
  }

  private getHeldDirections(): Record<Direction, boolean> {
    return {
      up: this.cursors.up.isDown || this.keyW.isDown || virtualInput.isDirectionActive('up'),
      down: this.cursors.down.isDown || this.keyS.isDown || virtualInput.isDirectionActive('down'),
      left: this.cursors.left.isDown || this.keyA.isDown || virtualInput.isDirectionActive('left'),
      right: this.cursors.right.isDown || this.keyD.isDown || virtualInput.isDirectionActive('right'),
    };
  }

  update(): void {
    this.handleMovement();
    this.handleInteract();
    this.handleB();
  }

  private handleMovement(): void {
    if (this.player.isMoving) return;

    const held = this.getHeldDirections();
    const direction = DIRECTION_ORDER.find((dir) => held[dir]);
    if (!direction) return;

    const next = getNextTile(this.player.tile, direction);

    if (canMoveTo(next, this.movementContext)) {
      this.player.startMove(direction);
    } else {
      this.player.face(direction);
    }
  }

  private handleInteract(): void {
    const pressed = Phaser.Input.Keyboard.JustDown(this.keyInteract) || virtualInput.consumeA();
    if (!pressed) return;

    const facingTile = this.player.getFacingTile();
    const interactable = findInteractableAt(facingTile, this.interactables);

    if (interactable) {
      console.log(`[Interact] ${interactable.name}${interactable.message ? `: ${interactable.message}` : ''}`);
    } else {
      console.log('[Interact] Nothing here.');
    }
  }

  private handleB(): void {
    const pressed = Phaser.Input.Keyboard.JustDown(this.keyB) || virtualInput.consumeB();
    if (!pressed) return;

    console.log('[B] Reserved for a future menu/cancel action.');
  }
}
